import React, { FC, useState } from "react";
import {
  Grid,
  Link,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Select,
  MenuItem,
  Divider,
  TextField,
  Box,
} from "@mui/material";
import Modal from "./Modal";
import { AuthenticationContext } from "../Context";
import { getHeaders } from "../utils/HeaderHelper";
import { useFlash } from "../FlashContext";

interface Provider {
  id: string;
  name: string;
  first_name: string;
  last_name: string;
  middle_name?: string;
  provider_npi_number: string;
}

interface Props {
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  renderingProviders: Provider[];
  claimId: number;
  onProviderSaved?: (providerId: number) => void;
  isRequired?: boolean;
  providerRole?: string;
}

interface ReferringProviderData {
  provider_qual: string;
  provider_tax: string;
  first_name: string;
  last_name: string;
  middle_name?: string;
  npi: string;
  claim_id: number;
  is_in_organization: boolean;
}

const ReferringProviderModal: FC<Props> = ({
  modalOpen,
  setModalOpen,
  renderingProviders,
  claimId,
  onProviderSaved,
  isRequired = false,
  providerRole = "referring",
}) => {
  const [isInOrganization, setIsInOrganization] = useState<string>("yes");
  const [selectedProvider, setSelectedProvider] = useState<string>("");
  const [providerQualifier, setProviderQualifier] = useState<string>(
    providerRole || "referring"
  );
  const [firstName, setFirstName] = useState<string>("");
  const [middleName, setMiddleName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [providerNPI, setProviderNPI] = useState<string>("");
  const [providerTaxonomy, setProviderTaxonomy] = useState<string>("");
  const authenticationSetting = React.useContext(AuthenticationContext);
  const { showToast } = useFlash();

  const handleClose = () => {
    setModalOpen(false);
  };

  const handleSave = () => {
    // Validate required fields
    if (isInOrganization === "yes") {
      if (!selectedProvider) {
        showToast({
          title: "Provider Required",
          text: "Please select a provider from the dropdown",
          type: "error",
        });
        return;
      }

      if (!/^[A-Za-z0-9]{10}$/.test(providerTaxonomy)) {
        showToast({
          title: "Invalid Provider Taxonomy",
          text: "Provider Taxonomy is required and must be 10 characters (letters and numbers)",
          type: "error",
        });
        return;
      }
    } else {
      if (!firstName || firstName.length < 1) {
        showToast({
          title: "First Name Required",
          text: "First Name is required",
          type: "error",
        });
        return;
      }

      if (!middleName || middleName.length < 1) {
        showToast({
          title: "Middle Name Required",
          text: "Middle Name is required",
          type: "error",
        });
        return;
      }

      if (!lastName || lastName.length < 1) {
        showToast({
          title: "Last Name Required",
          text: "Last Name is required",
          type: "error",
        });
        return;
      }

      if (!firstName || firstName.length < 1) {
        showToast({
          title: "First Name Required",
          text: "First Name is required",
          type: "error",
        });
        return;
      }

      if (!/^\d{10}$/.test(providerNPI)) {
        showToast({
          title: "Invalid Provider NPI",
          text: "Provider NPI is required and must be a valid 10-digit number",
          type: "error",
        });
        return;
      }

      if (!/^[A-Za-z0-9]{10}$/.test(providerTaxonomy)) {
        showToast({
          title: "Invalid Provider Taxonomy",
          text: "Provider Taxonomy is required and must be 10 characters (letters and numbers)",
          type: "error",
        });
        return;
      }
    }

    // Create the referring provider data
    const referringProviderData: ReferringProviderData = {
      provider_qual: providerQualifier,
      provider_tax: providerTaxonomy,
      first_name: "",
      last_name: "",
      npi: "",
      claim_id: claimId || null, // Use claimId if present, otherwise null
      is_in_organization: isInOrganization === "yes",
    };

    if (isInOrganization === "yes") {
      // Find the selected provider from the list
      const selectedProviderData = renderingProviders.find(
        (p) => p.id === selectedProvider
      );
      if (selectedProviderData) {
        referringProviderData.first_name = selectedProviderData.first_name;
        referringProviderData.last_name = selectedProviderData.last_name;
        referringProviderData.middle_name = selectedProviderData.middle_name;
        referringProviderData.npi = selectedProviderData.provider_npi_number;
      }
    } else {
      // Use the manually entered data
      referringProviderData.first_name = firstName;
      referringProviderData.last_name = lastName;
      referringProviderData.middle_name = middleName;
      referringProviderData.npi = providerNPI;
    }

    // Save the referring provider
    fetch("/referring_provider_snapshots", {
      method: "POST",
      headers: {
        ...getHeaders(authenticationSetting.csrfToken),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        referring_provider_snapshot: referringProviderData,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          showToast({
            title: "Failed to Save Provider",
            text: "Failed to save additional provider",
            type: "error",
          });
        }
        return response.json();
      })
      .then((data) => {
        showToast({
          title: "Provider Saved",
          text: "Additional provider saved successfully",
          type: "success",
        });
        if (onProviderSaved) {
          onProviderSaved(data.id);
        }
        setModalOpen(false);
      })
      .catch((error) => {
        showToast({
          title: "Failed to Save Provider",
          text: "Failed to save additional provider",
          type: "error",
        });
      });
  };

  const renderProviderFields = () => {
    if (isInOrganization === "yes") {
      return (
        <>
          <Typography
            sx={{
              color: "#707070",
              fontSize: "12px",
              lineHeight: "16px",
              marginBottom: "8px",
              fontWeight: 400,
            }}
          >
            Select a provider that is inside your organization as well as a
            providing qualifier.
          </Typography>
          <Typography
            sx={{
              color: "#A29D9B",
              fontWeight: 700,
              fontSize: "12px",
              lineHeight: "26px",
              marginBottom: "4px",
            }}
          >
            Select Provider*
          </Typography>
          <Box className="field-container" sx={{ marginLeft: "0px" }}>
            <FormControl fullWidth>
              <Select
                value={selectedProvider}
                onChange={(e) => setSelectedProvider(e.target.value)}
                displayEmpty
                size="small"
                className="the-field"
                required
                variant="outlined"
                sx={{
                  width: "100% !important",
                }}
                inputProps={{
                  sx: {
                    padding: "10px !important",
                  },
                }}
              >
                <MenuItem value="">
                  <em>Select provider</em>
                </MenuItem>
                {renderingProviders.map((provider) => (
                  <MenuItem key={provider.id} value={provider.id}>
                    {provider.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Grid item xs={12} sx={{ marginTop: "16px" }}>
            <Typography
              sx={{
                color: "#A29D9B",
                fontWeight: 700,
                fontSize: "12px",
                lineHeight: "26px",
              }}
            >
              Provider Taxonomy*
            </Typography>
            <Box className="field-container" sx={{ marginLeft: "0px" }}>
              <TextField
                fullWidth
                value={providerTaxonomy}
                onChange={(e) =>
                  setProviderTaxonomy(e.target.value.replace(/[^A-Za-z0-9]/g, "").slice(0, 10))
                }
                size="small"
                className="the-field"
                required
                variant="outlined"
                placeholder="Enter provider taxonomy"
                autoComplete="off"
                sx={{
                  width: "100% !important",
                }}
                InputProps={{
                  sx: {
                    "& input": {
                      padding: "10px !important",
                    },
                  },
                }}
                inputProps={{ maxLength: 10 }}
              />
            </Box>
          </Grid>
        </>
      );
    } else {
      return (
        <>
          <Typography
            sx={{
              color: "#707070",
              fontSize: "12px",
              lineHeight: "16px",
              marginBottom: "8px",
              fontWeight: 400,
            }}
          >
            Enter the following information about the provider that is outside
            of your organization.
          </Typography>

          {/* Name Fields */}
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Typography
                sx={{
                  color: "#A29D9B",
                  fontWeight: 700,
                  fontSize: "12px",
                  lineHeight: "26px",
                }}
              >
                First Name*
              </Typography>
              <Box className="field-container" sx={{ marginLeft: "0px" }}>
                <TextField
                  fullWidth
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  size="small"
                  className="the-field"
                  required
                  autoComplete="new-password"
                  variant="outlined"
                  placeholder="Enter first name"
                  sx={{
                    width: "100% !important",
                  }}
                  InputProps={{
                    sx: {
                      "& input": {
                        padding: "10px !important",
                      },
                    },
                  }}
                  slotProps={{
                    htmlInput: { maxLength: 20 },
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Typography
                sx={{
                  color: "#A29D9B",
                  fontWeight: 700,
                  fontSize: "12px",
                  lineHeight: "26px",
                }}
              >
                Middle Name*
              </Typography>
              <Box className="field-container" sx={{ marginLeft: "0px" }}>
                <TextField
                  fullWidth
                  value={middleName}
                  onChange={(e) => setMiddleName(e.target.value)}
                  size="small"
                  className="the-field"
                  variant="outlined"
                  placeholder="Enter middle name"
                  autoComplete="off"
                  sx={{
                    width: "100% !important",
                  }}
                  InputProps={{
                    sx: {
                      "& input": {
                        padding: "10px !important",
                      },
                    },
                  }}
                  slotProps={{
                    htmlInput: { maxLength: 20 },
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Typography
                sx={{
                  color: "#A29D9B",
                  fontWeight: 700,
                  fontSize: "12px",
                  lineHeight: "26px",
                }}
              >
                Last Name*
              </Typography>
              <Box className="field-container" sx={{ marginLeft: "0px" }}>
                <TextField
                  fullWidth
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  size="small"
                  className="the-field"
                  required
                  variant="outlined"
                  placeholder="Enter last name"
                  autoComplete="new-password"
                  sx={{
                    width: "100% !important",
                  }}
                  InputProps={{
                    sx: {
                      "& input": {
                        padding: "10px !important",
                      },
                    },
                  }}
                  slotProps={{
                    htmlInput: { maxLength: 35 },
                  }}
                />
              </Box>
            </Grid>
          </Grid>

          {/* NPI and Taxonomy in one row */}
          <Grid container spacing={1} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <Typography
                sx={{
                  color: "#A29D9B",
                  fontWeight: 700,
                  fontSize: "12px",
                  lineHeight: "26px",
                }}
              >
                Provider NPI*
              </Typography>
              <Box className="field-container" sx={{ marginLeft: "0px" }}>
                <TextField
                  fullWidth
                  value={providerNPI}
                  onChange={(e) =>
                    setProviderNPI(e.target.value.replace(/\D/g, ""))
                  }
                  size="small"
                  className="the-field"
                  required
                  variant="outlined"
                  placeholder="Enter provider NPI"
                  autoComplete="off"
                  sx={{
                    width: "100% !important",
                  }}
                  InputProps={{
                    sx: {
                      "& input": {
                        padding: "10px !important",
                      },
                    },
                  }}
                  inputProps={{ inputMode: "numeric", maxLength: 10 }}
                />
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Typography
                sx={{
                  color: "#A29D9B",
                  fontWeight: 700,
                  fontSize: "12px",
                  lineHeight: "26px",
                }}
              >
                Provider Taxonomy*
              </Typography>
              <Box className="field-container" sx={{ marginLeft: "0px" }}>
                <TextField
                  fullWidth
                  value={providerTaxonomy}
                  onChange={(e) =>
                    setProviderTaxonomy(e.target.value.replace(/[^A-Za-z0-9]/g, "").slice(0, 10))
                  }
                  size="small"
                  className="the-field"
                  required
                  variant="outlined"
                  placeholder="Enter provider taxonomy"
                  autoComplete="off"
                  sx={{
                    width: "100% !important",
                  }}
                  InputProps={{
                    sx: {
                      "& input": {
                        padding: "10px !important",
                      },
                    },
                  }}
                  inputProps={{ maxLength: 10 }}
                />
              </Box>
            </Grid>
          </Grid>
        </>
      );
    }
  };

  const renderModalContent = () => {
    return (
      <div
        style={{ textAlign: "left", paddingLeft: "20px", paddingRight: "20px" }}
      >
        <Grid container spacing={2}>
          {/* Is Provider in Organization */}
          <Grid item xs={12}>
            <Typography
              sx={{
                color: "#707070",
                fontWeight: 700,
                fontSize: "14px",
                lineHeight: "20px",
                marginBottom: "4px",
              }}
            >
              Is the Provider in your organization?
            </Typography>
            <FormControl component="fieldset">
              <RadioGroup
                row
                value={isInOrganization}
                onChange={(e) => setIsInOrganization(e.target.value)}
              >
                <FormControlLabel
                  value="yes"
                  control={<Radio />}
                  label="Yes"
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      color: "#707070",
                      fontSize: "14px",
                    },
                  }}
                />
                <FormControlLabel
                  value="no"
                  control={<Radio />}
                  label="No"
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      color: "#707070",
                      fontSize: "14px",
                    },
                  }}
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ width: "100%", marginBottom: "16px" }} />
          </Grid>

          {/* Dynamic Provider Fields */}
          <Grid item xs={12}>
            {renderProviderFields()}
          </Grid>

          {/* Provider Qualifier */}
          <Grid item xs={12}>
            <Typography
              sx={{
                color: "#707070",
                fontWeight: 700,
                fontSize: "14px",
                lineHeight: "20px",
                marginBottom: "4px",
              }}
            >
              Provider Qualifier
            </Typography>
            <FormControl component="fieldset">
              <RadioGroup
                row
                value={providerQualifier}
                onChange={(e) => setProviderQualifier(e.target.value)}
              >
                <FormControlLabel
                  value="referring"
                  control={
                    <Radio
                      disabled={isRequired && providerRole !== "referring"}
                    />
                  }
                  label="Referring"
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      color: "#707070",
                      fontSize: "14px",
                    },
                  }}
                />
                <FormControlLabel
                  value="ordering"
                  control={
                    <Radio
                      disabled={isRequired && providerRole !== "ordering"}
                    />
                  }
                  label="Ordering"
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      color: "#707070",
                      fontSize: "14px",
                    },
                  }}
                />
                <FormControlLabel
                  value="supervising"
                  control={
                    <Radio
                      disabled={isRequired && providerRole !== "supervising"}
                    />
                  }
                  label="Supervising"
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      color: "#707070",
                      fontSize: "14px",
                    },
                  }}
                />
              </RadioGroup>
            </FormControl>
          </Grid>
        </Grid>
      </div>
    );
  };

  return (
    <Modal
      successModalOpen={modalOpen}
      setSuccessModalOpen={setModalOpen}
      successHeader="Additional Provider"
      successContent={renderModalContent()}
      successCallback={handleSave}
      closeCallback={handleClose}
      confirmButtonText="Save Provider"
      width="600px"
      padding="10px"
    />
  );
};

export default ReferringProviderModal;
